import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Routes.scss";
import "./ReceiveOrders.scss";
import "./ReceiveReturns.scss";
import { Button, Grid } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Drawer from "@mui/material/Drawer";
import { Card, CardContent, IconButton, InputBase, Paper, Tooltip, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Constants from "../Common/Constants";
import LabelText from "../Common/LabelText";
import Utils from "../Common/Utils";
import IdentityManager from "../Managers/Identity.manager";
import OrderManager from "../Managers/Orders.Manager";
import PFManager from "../Managers/Process.Fulfillment.Manager";
import { IButton, INotificationPopupSettings } from "../Models/INotificationPopupSettings";
import allActions from "../Store/Actions/AllActions";
import { CallReceived, CloseIcon, SearchIcon, UnfoldMoreSharpIcon } from "../Subcomponent/Shared/SharedIcons";
import { NotificationPopup } from "../Subcomponent/Shared/NotificationPopup";
import { OrderDetailNotificationPopup } from "../Subcomponent/Shared/OrderDetailNotificationPopup";

const ReceiveReturns = (props: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userSettings = useSelector((state: any) => state.ActionLoaderReducersState.userSettings);
  const [receiveReturnsRows, setReceiveReturnsRows] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(userSettings?.pagination ?? Constants.UserSettingsDefault.Pagination);
  const [mobilePage, setMobilePage] = useState(0);
  const [noMobileRecords, setNoMobileRecords] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTracking, setSearchTracking] = useState("");
  const [openDetailDrawer, setOpenDetailDrawer] = useState(false);
  const [rowDetail, setRowDetail] = useState({} as any);
  const [errorMsg, setErrorMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openCarrierMsg, setCarrierMsg] = useState(false);

  const showActionLoader = useCallback(() => {
    dispatch(allActions.actionLoaderActions.showLoader());
  }, [dispatch]);

  const hideActionLoader = useCallback(() => {
    dispatch(allActions.actionLoaderActions.hideLoader());
  }, [dispatch]);

  const getOrder = useCallback((row: any) => row?._source?.Shipment ?? {}, []);

  const getLines = useCallback((row: any) => {
    const lines = getOrder(row)?.receiveReturnLines ?? getOrder(row)?.ShipmentLines?.ShipmentLine ?? [];
    return Array.isArray(lines) ? lines : [lines];
  }, [getOrder]);

  const getTrackingNumber = useCallback((row: any) => {
    const order = getOrder(row);
    if (order?.receiveReturnsTrackingNo) {
      return order.receiveReturnsTrackingNo;
    }
    if (order?.ExtnDisplayTrackingNo || order?.ExtnTrackingNo) {
      return order?.ExtnDisplayTrackingNo || order?.ExtnTrackingNo;
    }

    const trackingNumbers = getLines(row)
      .map((line: any) => line?.Extn?.ExtnDisplayTrackingNo || line?.ExtnDisplayTrackingNo || line?.Extn?.ExtnTrackingNo || line?.ExtnTrackingNo)
      .filter(Boolean);

    return trackingNumbers.filter((trackingNumber: any, index: number) => {
      return trackingNumbers.indexOf(trackingNumber) === index;
    }).join(", ");
  }, [getLines, getOrder]);

  const getTrackingNumbers = useCallback((row: any) => {
    const trackingNumbers = getTrackingNumber(row)
      .split(/[;,]/)
      .map((trackingNumber: string) => trackingNumber.trim())
      .filter(Boolean);

    return trackingNumbers.filter((trackingNumber: string, index: number) => trackingNumbers.indexOf(trackingNumber) === index);
  }, [getTrackingNumber]);

  const redirectToCarrier = (carrier: any, trackNo: any) => {
    const carrierURL = Utils.getCarrierURL(carrier);
    if (carrierURL) {
      const carrierService = carrierURL + trackNo;
      window.open(carrierService, "_blank");
    } else {
      setCarrierMsg(true);
    }
  };

  const getCarrier = async (trackNo: any) => {
    try {
      showActionLoader();
      let carrierResult = "";
      let carrier = await PFManager.getCarrierFromShippingCenter(trackNo);
      hideActionLoader();
      if (carrier?.success) {
        carrierResult = carrier?.data
          ? carrier?.data
          : Utils.findCarrier(trackNo);
      } else {
        carrierResult = Utils.findCarrier(trackNo);
      }
      redirectToCarrier(carrierResult, trackNo);
    } catch (error: any) {
      hideActionLoader();
      console.log("Get Carrier Error", error);
      const msg = LabelText.errorMessage;
      setErrorMsg(msg);
      setOpenError(true);
    }
  };

  const renderTrackingNumbers = (row: any) => {
    const trackingNumbers = getTrackingNumbers(row);

    return trackingNumbers.map((trackingNumber: string, index: number) => (
      <React.Fragment key={`${trackingNumber}-${index}`}>
        {index > 0 ? ", " : ""}
        <span
          className="clickable-copyable-link"
          onClick={(event) => {
            event.stopPropagation();
            getCarrier(trackingNumber);
          }}
        >
          {trackingNumber}
        </span>
      </React.Fragment>
    ));
  };

  const renderTableTrackingNumber = (row: any) => {
    const trackingNumbers = getTrackingNumbers(row);
    const firstTrackingNumber = trackingNumbers[0] ?? "-";
    const hasMultipleTrackingNumbers = trackingNumbers.length > 1;

    return (
      <Tooltip title={hasMultipleTrackingNumbers ? trackingNumbers.join(", ") : ""}>
        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          <span
            className="clickable-copyable-link"
            onClick={(event) => {
              event.stopPropagation();
              getCarrier(firstTrackingNumber);
            }}
          >
            {firstTrackingNumber}
          </span>
          {hasMultipleTrackingNumbers ? " ..." : ""}
        </div>
      </Tooltip>
    );
  };

  const renderMobileTrackingNumber = (row: any) => {
    const trackingNumbers = getTrackingNumbers(row);
    const firstTrackingNumber = trackingNumbers[0] ?? "-";

    return (
      <span
        className="clickable-copyable-link"
        onClick={(event) => {
          event.stopPropagation();
          getCarrier(firstTrackingNumber);
        }}
      >
        {firstTrackingNumber}{trackingNumbers.length > 1 ? " ..." : ""}
      </span>
    );
  };

  const getRemainingQty = useCallback((row: any) => {
    const order = getOrder(row);
    if (order?.remainingQtyCountCalculated !== undefined) {
      return order.remainingQtyCountCalculated;
    }

    return getLines(row).reduce((total: number, line: any) => {
      return total + Number(line?.RemainingQty ?? 0);
    }, 0);
  }, [getLines, getOrder]);

  const getStoreForRow = useCallback((row: any) => {
    const order = getOrder(row);
    const firstLine = getLines(row)?.[0] ?? {};
    return firstLine?.ReceivingNode || order?.ShipNode || "";
  }, [getLines, getOrder]);

  const getCustomerName = useCallback((row: any) => {
    const order = getOrder(row);
    const customer = order?.PersonInfoShipFrom ?? order?.CustomerInfo ?? {};
    const fullName = [customer?.FirstName, customer?.LastName].filter(Boolean).join(" ").trim();
    return fullName || customer?.Company || "-";
  }, [getOrder]);

  const getStatusLabel = (row: any) => {
    const order = getOrder(row);
    if (order?.StatusName === Constants.ReceiveReturnsStatus.ReturnClosed && order?.Status === Constants.ReceiveReturnsStatus.ReturnClosedCode) {
      return "Return Closed";
    }
    if (order?.StatusName === Constants.ReceiveReturnsStatus.ReturnWithCarrier && order?.Status === Constants.ReceiveReturnsStatus.ReturnWithCarrierCode) {
      return "Return Created";
    }
    return order?.StatusName || order?.StatusDesc || order?.Status || "";
  }

  const getCreatedDateValue = useCallback((row: any) => {
    return getOrder(row)?.OrderDate ?? "";
  }, [getOrder]);

  const getCreatedDate = useCallback((row: any) => {
    const orderDate = getCreatedDateValue(row);
    return orderDate ? Utils.convertElasticDateToPst(orderDate) : "";
  }, [getCreatedDateValue]);

  const getAgeFromOrderDate = useCallback((row: any) => {
    const orderDate = getCreatedDateValue(row);
    if (!orderDate) {
      return 0;
    }

    const createdDate = new Date(orderDate);
    if (Number.isNaN(createdDate.getTime())) {
      return 0;
    }

    const age = Math.floor((new Date().getTime() - createdDate.getTime()) / 86400000);
    return age > 0 ? age : 0;
  }, [getCreatedDateValue]);

  const getAgeLabel = useCallback((row: any) => {
    const age = getAgeFromOrderDate(row);
    return age <= 0 ? "Today" : `${age} Day(s)`;
  }, [getAgeFromOrderDate]);

  const getItemDescription = useCallback((row: any) => {
    const firstLine = getLines(row)?.[0] ?? {};
    return firstLine?.ItemDesc ?? "";
  }, [getLines]);

  const getShortItemDescription = useCallback((row: any) => {
    const itemDescription = getItemDescription(row);
    const words = itemDescription.split(" ").filter(Boolean);
    const wordLimit = Utils.isMobile ? 6 : 12;
    if (words.length <= wordLimit) {
      return itemDescription;
    }

    return `${words.slice(0, wordLimit).join(" ")}...`;
  }, [getItemDescription]);

  const filteredRows = useMemo(() => receiveReturnsRows, [receiveReturnsRows]);

  const paginated = useMemo(() => {
    return filteredRows;
  }, [filteredRows, page, rowsPerPage]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    if (!Utils.isMobile) {
      loadReceiveReturns(false, newPage * rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const pageSize = parseInt(event.target.value, 10);
    setRowsPerPage(pageSize);
    setPage(0);
    updatePaginationSettings(pageSize);
  };

  const updatePaginationSettings = (pageSize: number) => {
    const updatedSettings = {
      ...userSettings,
      pagination: pageSize,
    };
    dispatch(allActions.actionLoaderActions.setUserSettings(updatedSettings));

    Utils.updateUserSettings({
      attributeName: Constants.UserSettingsFields.Pagination,
      value: pageSize,
      userName: userSettings.userName,
    });
  };

  const mergeRows = (existingRows: any[], newRows: any[]) => {
    const existingIds = existingRows.map((row: any) => row?._id);
    const rowsToAppend = newRows.filter((row: any) => !existingIds.includes(row?._id));
    return [...existingRows, ...rowsToAppend];
  };

  const loadReceiveReturns = useCallback((append = false, from = 0, overrideSize?: number) => {
    const storeId = IdentityManager.getMultipleStoresId(props.store);
    const trackingSearch = searchTracking.trim();

    if (trackingSearch.length > 0 && trackingSearch.length < 3) {
      if (!append) {
        setReceiveReturnsRows([]);
        setTotalRows(0);
      }
      setNoMobileRecords(true);
      return;
    }

    if (!storeId || (Array.isArray(storeId) && storeId.length === 0)) {
      if (!append) {
        setReceiveReturnsRows([]);
        setTotalRows(0);
      }
      setNoMobileRecords(true);
      return;
    }

    const pageSize = overrideSize ?? (Utils.isMobile ? Constants.FulfillOrdersGrid.MobileRowsPerPage : rowsPerPage);
    setIsLoading(true);
    showActionLoader();

    OrderManager.getReceiveReturns(storeId, { from, size: pageSize, searchTracking: trackingSearch }).then((resultResponse: any) => {
      if (resultResponse?.success) {
        const responseRows = resultResponse?.data?.hits?.hits ?? [];
        const totalCount = Number(resultResponse?.data?.hits?.total?.value ?? responseRows.length);
        responseRows.forEach((element: any) => {
          if (element?._source?.Shipment) {
            element._source.Shipment.Age = getAgeFromOrderDate(element);
          }
        });
        if (!trackingSearch) {
          dispatch(allActions.actionLoaderActions.setReceiveReturnsTotalQuantity(
            Number(resultResponse?.data?.hits?.totalReceiveReturnLines ?? 0)
          ));
        }
        const rows = responseRows.slice(0, pageSize);
        setTotalRows(totalCount);
        if (append) {
          setReceiveReturnsRows((currentRows: any[]) => {
            if (rows.length === 0 && currentRows.length > 0) {
              setNoMobileRecords(true);
              return currentRows;
            }
            return mergeRows(currentRows, rows);
          });
        }
        else {
          setReceiveReturnsRows(rows);
        }
        setNoMobileRecords(rows.length < pageSize);
      }
      else {
        setReceiveReturnsRows([]);
        setTotalRows(0);
        setNoMobileRecords(true);
        setErrorMsg("Something went wrong please contact the IT Help Desk for assistance");
        setOpenError(true);
      }
      setIsLoading(false);
      hideActionLoader();
    }).catch((error: any) => {
      console.log(error);
      setReceiveReturnsRows([]);
      setTotalRows(0);
      setNoMobileRecords(true);
      setIsLoading(false);
      setErrorMsg("Something went wrong please contact the IT Help Desk for assistance");
      setOpenError(true);
      hideActionLoader();
    });
  }, [dispatch, getAgeFromOrderDate, hideActionLoader, props.store, rowsPerPage, searchTracking, showActionLoader]);

  const loadMoreMobileRows = useCallback(() => {
    if (!Utils.isMobile || isLoading || noMobileRecords) {
      return;
    }

    const nextPage = mobilePage + 1;
    const nextFrom = nextPage * Constants.FulfillOrdersGrid.MobileRowsPerPage;
    setMobilePage(nextPage);
    loadReceiveReturns(true, nextFrom);
  }, [isLoading, loadReceiveReturns, mobilePage, noMobileRecords]);

  const openDetailPage = (row: any) => {
    const params = new URLSearchParams();
    const order = getOrder(row);
    const detailKey = order?.OrderHeaderKey || order?.OrderNo;

    if (order?.OrderNo) {
      params.set("orderno", order.OrderNo);
    }
    if (order?.OrderHeaderKey) {
      params.set("shipmentkey", order.OrderHeaderKey);
    }
    const store = getStoreForRow(row);
    if (store) {
      params.set("store", store);
    }
    params.set("ExtnTrackingNo", order.ExtnTrackingNo);
    if (detailKey) {
      try {
        sessionStorage.setItem(`receiveReturnDetail:${detailKey}`, JSON.stringify(row));
      }
      catch (error) {
        console.log(error);
      }
    }

    navigate(`/ReceiveReturnDetail?${params.toString()}`, { state: { row } });
  };

  const openDetailDrawerClick = (row: any) => {
    setRowDetail(row);
    setOpenDetailDrawer(true);
  };

  const getColumns = () => {
    const columnWidths = props.store?.length > 1 ?
      {
        actions: "200px",
        order: "140px",
        customer: "165px",
        createdDate: "200px",
        age: "105px",
        item: "240px",
        remainingQty: "130px",
        store: "85px",
        tracking: "255px",
      } :
      {
        actions: "200px",
        order: "140px",
        customer: "165px",
        createdDate: "200px",
        age: "85px",
        item: "240px",
        remainingQty: "130px",
        store: "0%",
        tracking: "255px",
      };
    return [
      {
        name: <span style={{ paddingLeft: 8 }}>Actions</span>,
        cell: (row: any) => (
          <div style={{ paddingLeft: 8, width: "100%", boxSizing: "border-box" }}>
            <Button className="red-outline-btn" startIcon={<CallReceived className="fTabIcons" />} variant="contained" color="primary" onClick={() => openDetailPage(row)}>
              Receive Returns
            </Button>
          </div>
        ),
        ignoreRowClick: true,
        width: columnWidths.actions,
      },
      {
        name: Constants.ReceiveReturnsLabels.ReturnOrderNumber,
        selector: (row: any) => getOrder(row)?.OrderNo,
        sortable: true,
        width: columnWidths.order,
      },
      {
        name: "Customer Name",
        selector: (row: any) => getCustomerName(row),
        sortable: true,
        width: columnWidths.customer,
      },
      {
        name: "Created Date",
        selector: (row: any) => getCreatedDateValue(row),
        cell: (row: any) => <span style={{ fontFamily: "Roboto Mono", fontSize: "13px" }}>{getCreatedDate(row)}</span>,
        sortable: true,
        width: columnWidths.createdDate,
      },
      {
        name: "Age",
        selector: (row: any) => getAgeFromOrderDate(row),
        cell: (row: any) => <span style={{ fontFamily: "Roboto Mono", fontSize: "13px" }}>{getAgeLabel(row)}</span>,
        sortable: true,
        width: columnWidths.age,
      },
      {
        name: "Item",
        selector: (row: any) => getItemDescription(row),
        cell: (row: any) => (
          <Tooltip title={getItemDescription(row)}>
            <Typography noWrap style={{ fontSize: "13px", maxWidth: "100%", paddingRight: "24px" }}>{getShortItemDescription(row)}</Typography>
          </Tooltip>
        ),
        width: columnWidths.item,
      },
      {
        name: "Remaining Qty",
        selector: (row: any) => getRemainingQty(row),
        sortable: true,
        width: columnWidths.remainingQty,
      },
      {
        name: "Store",
        selector: (row: any) => getStoreForRow(row),
        sortable: true,
        width: props.store?.length > 1 ? columnWidths.store : undefined,
        omit: props.store?.length <= 1,
      },
      {
        name: "Tracking #",
        selector: (row: any) => getTrackingNumber(row),
        cell: (row: any) => renderTableTrackingNumber(row),
        sortable: true,
        width: columnWidths.tracking,
      },
    ];
  };

  const renderWebDataTable = () => {
    return (
      <div className="pendingReceiptsDataTable receiveReturnsDataTable">
        <DataTable className="pendingReceiptsDataTable receiveReturnsDataTable" title="" data={paginated} columns={getColumns()} />
      </div>
    );
  };

  const renderMobileData = () => {
    const storeId = IdentityManager.getStoreId(props.store);
    if (paginated && paginated.length == 0) {
      return (
        <div style={{ marginTop: 10 }}>
          <Typography variant="body2">No Data to display</Typography>
          {!storeId ? <Typography variant="h5">Please select a Store #</Typography> : ""}
        </div>
      );
    }

    return (
      <>
        {paginated.map((row: any) => {
          const order = getOrder(row);
          return (
            <Card key={row?._id} className="receiveReturnsMobileCard">
              <CardContent style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}>
                <Grid container wrap="nowrap" style={{ margin: "0px", minHeight: 68 }}>
                  <Grid onClick={() => openDetailDrawerClick(row)} item xs={3} sm={3} className="receiveReturnsMobileOrder">
                    <Typography noWrap style={{ fontSize: "10px", fontWeight: 600 }}>{order?.OrderNo}</Typography>
                    <UnfoldMoreSharpIcon onClick={() => openDetailDrawerClick(row)} style={{ transform: "rotate(30deg)", position: "absolute", top: 0, right: 0, fontSize: "20px", color: "#e7131a" }} />
                  </Grid>
                  <Grid onClick={() => openDetailDrawerClick(row)} item xs={5} sm={5} className="receiveReturnsMobileSummary">
                    <Typography noWrap style={{ color: Constants.Colors.black, fontSize: "12px", fontWeight: 500 }}>{getShortItemDescription(row)}</Typography>
                    <Typography noWrap style={{ fontSize: "10px", fontWeight: 500 }}>{getCustomerName(row)}</Typography>
                    <Typography noWrap style={{ fontSize: "10px", lineHeight: 1.2 }}>{renderMobileTrackingNumber(row)}</Typography>
                    <Typography noWrap style={{ fontSize: "10px", lineHeight: 1.2 }}>{getCreatedDate(row)}</Typography>
                    <Typography noWrap style={{ fontSize: "10px", display: "flex", alignItems: "center", lineHeight: 1.2 }}>
                      <span style={{ color: "black", fontWeight: 500 }}>QTY:</span>
                      <span className="returnsMobileLabel" style={{ marginLeft: 2, marginRight: 5 }}>{getRemainingQty(row)}</span>
                      {props?.store?.length > 1 &&
                        <>
                          <span style={{ color: "black", fontWeight: 500 }}>Store:</span>
                          <span className="returnsMobileLabel" style={{ marginLeft: 2 }}>{getStoreForRow(row)}</span>
                        </>
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} className="receiveReturnsMobileButtonCell">
                    <Button onClick={() => openDetailPage(row)} className="mobile-list-btn receiveReturnsMobileAction">
                      <CallReceived style={{ fontSize: 16, marginRight: 2 }} />
                      <Typography noWrap style={{ fontSize: "10px" }}>Receive returns</Typography>
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}
        {isLoading && mobilePage > 0 && <Typography variant="body2" style={{ textAlign: "center", padding: 10 }}>Loading...</Typography>}
      </>
    );
  };

  const searchBar = () => {
    return (
      <Paper elevation={0} style={{ width: "auto", marginRight: Utils.isMobile ? 0 : 0, marginBottom: 0 }} className={!Utils.isMobile ? "searchWeb" : ""} sx={{ p: "2px 4px", display: "flex", alignItems: "center", background: "white", border: "1px solid #bbbbbb", borderRadius: "6px" }}>
        <IconButton type="submit" sx={{ p: "10px" }} aria-label="search"><SearchIcon /></IconButton>
        <InputBase
          value={searchTracking}
          sx={{ ml: 1, flex: 1 }}
          placeholder="Tracking #"
          inputProps={{ "aria-label": "search tracking number" }}
          style={{ marginLeft: 10 }}
          onChange={(e) => {
            setSearchTracking(e.target.value);
            setPage(0);
            setMobilePage(0);
            setNoMobileRecords(false);
          }}
        />
        <IconButton type="submit" sx={{ p: "10px" }} aria-label="clear search"><CloseIcon onClick={() => { setSearchTracking(""); setMobilePage(0); setNoMobileRecords(false); }} /></IconButton>
      </Paper>
    );
  };

  const pagination = () => {
    return (
      <div id="paginationWebId" className="pendingReceiptsPaginationWeb receiveReturnsToolbar">
        <div />
        <div>
          <Stack spacing={1}>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={totalRows} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} />
          </Stack>
        </div>
        <div className="receiveReturnsSearch">{searchBar()}</div>
      </div>
    );
  };

  const renderDetailDrawer = () => {
    const order = getOrder(rowDetail);
    const lines = getLines(rowDetail);

    return (
      <React.Fragment>
        <Card className="dCard" style={{ backgroundColor: "#F5F5F5" }}>
          <CardContent className={Utils.isMobile ? "" : "dCardContent"}>
            <div className="topHeading">
              <div className="gridHeading">
                <Typography style={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography className="card-heading">{Constants.ReceiveReturnsLabels.ReturnOrderDetails}</Typography>
                  <div onClick={() => setOpenDetailDrawer(false)}>
                    <CloseIcon className="card-heading" />
                  </div>
                </Typography>
              </div>
            </div>
            <Stack direction="row" style={{ marginTop: "20px", display: "flex", gap: "20", flexWrap: "wrap" }}>
              <Stack spacing={2} direction="column" style={{ flex: "60" }}>
                <Typography className="itemHeading">Tracking #</Typography>
                <Typography className="itemElement">{renderTrackingNumbers(rowDetail)}</Typography>
              </Stack>
            </Stack>
            <Stack direction="row" style={{ marginTop: "20px", display: "flex", gap: "20", flexWrap: "wrap" }}>
              <Stack spacing={2} direction="column" style={{ flex: "45" }}>
                <Typography className="itemHeading">Status</Typography>
                <Typography className="itemElement">{getStatusLabel(rowDetail)}</Typography>
              </Stack>
              {
                props.store?.length > 1 ?
                  <Stack spacing={2} direction="column" style={{ flex: "45" }}>
                    <Typography className="itemHeading">Store</Typography>
                    <Typography className="itemElement">{getStoreForRow(rowDetail)}</Typography>
                  </Stack>
                  : <></>
              }
            </Stack>
            <div style={{ marginLeft: Utils.isMobile ? "" : "50px", maxHeight: "450px", overflowY: "auto" }} className="expandedParent">
              {lines.map((line: any, index: number) => (
                <div
                  key={line?.OrderLineKey ?? index}
                  className={Utils.isMobile ? "expandedSectionMobile" : "expandedSection"}
                  style={Utils.isMobile ? { backgroundColor: "transparent", boxShadow: "none", borderRadius: 0, paddingTop: "10px", paddingBottom: "10px" } : {}}
                >
                  <Typography
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      width: !Utils.isMobile ? "600px" : "auto",
                      textAlign: "start",
                      fontFamily: "Roboto",
                      backgroundColor: "transparent",
                    }}
                  >
                    {line?.ItemDesc}
                  </Typography>
                  <div className="item" style={{ width: !Utils.isMobile ? "70px" : "auto", backgroundColor: "transparent" }}>
                    <Typography style={{ fontSize: "12px" }}>Item ID</Typography>
                    <Typography style={{ fontSize: "12px" }}>
                      <b style={{ fontFamily: "Roboto Mono" }}>{line?.ItemID || "-"}</b>
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </React.Fragment>
    );
  };

  const closeErrorPopup = () => {
    setOpenError(false);
  };

  const errorPopupCloseButtons: IButton[] = [{
    text: "Close",
    icon: <CloseIcon />,
    action: closeErrorPopup,
    color: Constants.Colors.red,
  }];

  const Error: INotificationPopupSettings = {
    open: openError,
    type: Constants.NotificationPopupType.Error,
    title: "Error",
    msg: errorMsg,
    draggable: false,
    handleClose: closeErrorPopup,
    actions: errorPopupCloseButtons,
  };

  const closeCarrierMsg = () => {
    setCarrierMsg(false);
  };

  let carrierMsgButtons: IButton[] = [
    {
      text: "Close",
      icon: <CloseIcon />,
      action: closeCarrierMsg,
      color: Constants.Colors.blue,
    },
  ];

  let carrierMsg: INotificationPopupSettings = {
    open: openCarrierMsg,
    type: Constants.NotificationPopupType.Info,
    title: "Info",
    msg: Utils.getCarrierMsg(),
    draggable: false,
    handleClose: closeCarrierMsg,
    actions: carrierMsgButtons,
  };

  useEffect(() => {
    setPage(0);
    setMobilePage(0);
    setNoMobileRecords(false);
    loadReceiveReturns(false, 0);
  }, [loadReceiveReturns]);

  useEffect(() => {
    if (!Utils.isMobile) {
      return;
    }

    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 100) {
        loadMoreMobileRows();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loadMoreMobileRows]);

  return (
    <div>
      <NotificationPopup {...Error} />
      <OrderDetailNotificationPopup {...carrierMsg}></OrderDetailNotificationPopup>
      <div className={Utils.isMobile ? "innerContianerMobile" : "receiveReturnsContainer"}>
        {Utils.isMobile ? <Box sx={{ p: 3 }} style={{ textAlign: "left", padding: "5px", paddingLeft: "5px", paddingTop: "5px" }}>{searchBar()}</Box> : <></>}
        {!Utils.isMobile ? pagination() : <></>}
        {!Utils.isMobile ? renderWebDataTable() : renderMobileData()}
      </div>
      <Drawer anchor="bottom" open={openDetailDrawer} onClose={() => setOpenDetailDrawer(false)} style={{ zIndex: 9999999 }}>
        {renderDetailDrawer()}
      </Drawer>
    </div>
  );
};

export default ReceiveReturns;
